import { Layout as AntLayout } from "antd";
import { useLayoutEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Paths } from "../../../router/paths";
import { splitLocalePath } from "../../../i18n/localePath";
import { useAnnualDiscountPromo } from "../../../hooks/useAnnualDiscountPromo";
import { AnnualDiscountBanner } from "../../components/AnnualDiscountBanner/AnnualDiscountBanner";
import { PaymentFailedBanner } from "../../components/PaymentFailedBanner/PaymentFailedBanner";
import { TrialReadyModal } from "../../components/TrialReadyModal/TrialReadyModal";
import { useTrialReadyModal } from "../../../hooks/useTrialReadyModal";
import { usePaymentPastDue } from "../../../hooks/usePaymentPastDue";
import { AnnualDiscountModal } from "../../components/AnnualDiscountModal/AnnualDiscountModal";
import { Header } from "./Header";
import { Sidebar } from "./SideBar";
import { SidebarProvider } from "./SideBar/SidebarContext";
import { useSidebar } from "./SideBar/useSidebar";
import { NotificationWebSocketProvider } from "./NotificationWebSocketProvider";
import { LandingFooter } from "../../pages/Home/components/landing/LandingFooter";
import styles from "./index.module.css";

const { Content } = AntLayout;

const EMBEDDED_APP_PATHS: readonly string[] = [
  Paths.AKINDA_AI,
  Paths.GAMES,
];

/** Same footer as home (LandingFooter): full width, logo, nav, badges, legal block, socials */
const LANDING_STYLE_FOOTER_PATHS: readonly string[] = [
  Paths.ABOUT_US,
  Paths.ABOUT_FOUNDER,
  Paths.FOR_BUSINESS,
  Paths.MCP,
  Paths.STARTUPS,
  Paths.REFERRAL,
  Paths.STOCK_SCREENER,
  Paths.ETF_SCREENER,
  Paths.OUR_MISSION,
  Paths.CONTACT,
  Paths.SHARIAH_SCREENING_METHODOLOGY,
  Paths.PRIVACY_POLICY,
  Paths.TERMS_AND_CONDITIONS,
  Paths.DISCLOSURES,
];

/** Wide pages that start with the desktop sidebar collapsed (user can expand). */
const COLLAPSED_SIDEBAR_PATHS: readonly string[] = [
  Paths.MCP,
  Paths.STARTUPS,
  Paths.REFERRAL,
];

/** One-fund pages (/etf/:symbol) share the ETF screener's footer. */
const isEtfFundPath = (pathname: string) => pathname.startsWith("/etf/");

const routeForcesSidebarCollapse = (pathname: string) =>
  COLLAPSED_SIDEBAR_PATHS.includes(pathname) ||
  pathname.startsWith("/partners/");

const LayoutContent = () => {
  const { pathname: urlPathname } = useLocation();
  const pathname = splitLocalePath(urlPathname).pathname;
  const { isCollapsed, setCollapsed } = useSidebar();
  const sidebarPreferenceRef = useRef<boolean | null>(null);
  const appliedDefaultCollapseRef = useRef(false);
  const showLandingFooter =
    LANDING_STYLE_FOOTER_PATHS.includes(pathname) ||
    pathname.startsWith("/partners/") ||
    isEtfFundPath(pathname);
  const isEmbeddedApp = EMBEDDED_APP_PATHS.includes(pathname);
  const promo = useAnnualDiscountPromo();
  const { isPastDue } = usePaymentPastDue();
  const trialModal = useTrialReadyModal();
  const collapseSidebarByDefault = routeForcesSidebarCollapse(pathname);

  useLayoutEffect(() => {
    if (collapseSidebarByDefault) {
      if (!appliedDefaultCollapseRef.current) {
        sidebarPreferenceRef.current = isCollapsed;
        appliedDefaultCollapseRef.current = true;
        setCollapsed(true);
      }
      return;
    }

    if (appliedDefaultCollapseRef.current) {
      appliedDefaultCollapseRef.current = false;
      if (sidebarPreferenceRef.current !== null) {
        setCollapsed(sidebarPreferenceRef.current);
        sidebarPreferenceRef.current = null;
      }
    }
  }, [collapseSidebarByDefault, isCollapsed, setCollapsed]);

  return (
    <NotificationWebSocketProvider>
      <div className={styles.layoutWrapper}>
        {/* A failed payment outranks marketing: never stack both bars. */}
        {isPastDue ? (
          <PaymentFailedBanner />
        ) : (
          promo.showBar && <AnnualDiscountBanner percent={promo.percent} />
        )}
        <div className={styles.mainRow}>
          <AntLayout className={styles.layout}>
            <Sidebar />
            <Content className={styles.content}>
              <Header />
              <div
                className={
                  isEmbeddedApp
                    ? styles.contentScrollEmbedded
                    : styles.contentScroll
                }
              >
                <div
                  className={
                    isEmbeddedApp
                      ? styles.contentWrapperEmbedded
                      : styles.contentWrapper
                  }
                >
                  <Outlet />
                </div>
              </div>
            </Content>
          </AntLayout>
        </div>
        {showLandingFooter && (
          <div
            className={
              pathname === Paths.STOCK_SCREENER ||
              pathname === Paths.ETF_SCREENER ||
              isEtfFundPath(pathname)
                ? styles.stockScreenerFooterWrap
                : undefined
            }
          >
            <LandingFooter />
          </div>
        )}
        <TrialReadyModal
          open={trialModal.showModal}
          onDismiss={trialModal.dismissModal}
        />
        {/* A user who can start a trial gets the trial popup, not the annual pitch. */}
        <AnnualDiscountModal
          open={promo.showModal && !trialModal.isEligible}
          percent={promo.percent}
          onDismiss={promo.dismissModal}
        />
      </div>
    </NotificationWebSocketProvider>
  );
};

export const Layout = () => {
  return (
    <SidebarProvider>
      <LayoutContent />
    </SidebarProvider>
  );
};
